/**
 * Shopify Quantity Gifts Manager
 * A clean, robust system for managing quantity-based gift functionality
 */

class CartAPI {
  static async getCart() {
    const response = await fetch("/cart.js");
    if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
    return response.json();
  }

  static validateQuantity(quantity) {
    if (quantity < 0) throw new Error("Quantity cannot be negative");
    if (!Number.isInteger(Number(quantity)))
      throw new Error("Quantity must be an integer");
    return parseInt(quantity);
  }

  static async addItem(variantId, quantity) {
    if (!variantId) throw new Error("Variant ID is required");
    const validQuantity = this.validateQuantity(quantity);

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: parseInt(variantId),
        quantity: validQuantity,
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Add item failed: ${response.status} - ${error}`);
    }

    return response.json();
  }

  static async updateItem(itemId, quantity) {
    if (!itemId) throw new Error("Item ID is required");
    const validQuantity = this.validateQuantity(quantity);

    const id = String(itemId).includes(":") ? itemId : parseInt(itemId);

    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: id,
        quantity: validQuantity,
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Update item failed: ${response.status} - ${error}`);
    }

    return response.json();
  }
}

class GiftManager {
  constructor() {
    this.isProcessing = false;
    this.isSyncing = false;
    this.isGiftSyncPaused = false;
    this.resumeTimeout = null;
    this.debounceTime = 500;
    this.retryAttempts = 3;
    this.storageKey = this.getThemeSpecificKey();
    this.hasRunCleanup = false;
    this.lastRemoveTime = 0;
    this.lastSyncTime = 0;

    this.handleQuantityChange = this.debounce(
      this.handleQuantityChange.bind(this),
      this.debounceTime
    );
    this.handleProductAdd = this.handleProductAdd.bind(this);
    this.handleProductRemove = this.debounce(
      this.handleProductRemove.bind(this),
      this.debounceTime
    );
  }

  init() {
    if (this.isInitialized) return;

    if (!this.hasAnyGiftConfigurations() && !this.hasGiftBlocksOnPage()) {
      return;
    }

    this.bindEvents();

    if (!this.hasRunCleanup) {
      this.cleanupOrphanedData();
      this.hasRunCleanup = true;
    }

    this.isInitialized = true;

    setTimeout(() => this.syncGifts(), 100);
  }

  hasGiftBlocksOnPage() {
    return document.querySelector("quantity-gifts") !== null;
  }

  getThemeSpecificKey() {
    const themeId =
      (window.Shopify && window.Shopify.theme && window.Shopify.theme.id) ||
      window.location.hostname.replace(/\./g, "-");
    return `productGiftConfigurations_${themeId}`;
  }

  bindEvents() {
    document.addEventListener("click", async (e) => {
      if (e.target.matches(".quantity__button")) {
        e.stopPropagation();
        await this.handleQuantityChange(e);
      }

      if (
        e.target.matches("cart-remove-button") ||
        e.target.closest("cart-remove-button")
      ) {
        e.stopPropagation();
        await this.handleProductRemove(e);
      }
    });

    document.addEventListener("submit", async (e) => {
      if (e.target.matches('form[action*="/cart/add"]')) {
        const isVariantListForm = this.isVariantListForm(e.target);
        if (isVariantListForm) {
          this.pauseGiftSync(3000);
        } else {
          this.pauseGiftSync();
        }

        await this.handleProductAdd(e);
      }
    });

    document.addEventListener("cart:updated", () => {
      if (this.isGiftSyncPaused) {
        return;
      }

      const now = Date.now();
      if (now - this.lastSyncTime < 500) {
        return;
      }

      setTimeout(() => this.syncGifts(), 100);
    });
  }

  pauseGiftSync(duration = 2000) {
    this.isGiftSyncPaused = true;
    if (this.resumeTimeout) {
      clearTimeout(this.resumeTimeout);
    }

    this.resumeTimeout = setTimeout(() => {
      this.isGiftSyncPaused = false;
      this.syncGifts();
    }, duration);
  }

  isVariantListForm(form) {
    const quantityInputs = form.querySelectorAll('input[name*="quantity"]');
    const hasMultipleQuantities = quantityInputs.length > 1;

    const isInVariantList = form.closest("variant-list-element") !== null;

    const hasItemsArray = form.querySelector('input[name*="items["]') !== null;

    return hasMultipleQuantities || isInVariantList || hasItemsArray;
  }

  async handleQuantityChange() {
    if (!this.hasAnyGiftConfigurations()) return;
    await this.processGiftUpdate(() => this.syncAfterCartChange());
  }

  async handleProductAdd() {
    if (!this.hasAnyGiftConfigurations()) return;
    await this.processGiftUpdate(() => this.syncAfterCartChange(1000));
  }

  async handleProductRemove() {
    const now = Date.now();

    if (now - this.lastRemoveTime < 100) {
      return;
    }
    this.lastRemoveTime = now;


    if (!this.hasAnyGiftConfigurations()) return;

    if (this.isProcessing) {
      return;
    }

    await this.processGiftUpdate(() => this.syncAfterCartChange(), true);

  }

  async syncAfterCartChange(waitTime = 100) {
    await this.waitForCartUpdate(waitTime);
    await this.syncGifts();
  }

  hasAnyGiftConfigurations() {
    return Object.keys(this.getConfigurations()).length > 0;
  }

  async processGiftUpdate(updateFn, showLoader = false) {
    if (this.isProcessing) return;

    this.isProcessing = true;
    if (showLoader) this.showLoading();

    try {
      await updateFn();
    } catch (error) {
      this.showError("Failed to update gifts");
    } finally {
      this.isProcessing = false;
      if (showLoader) this.hideLoading();
    }
  }

  async waitForCartUpdate(maxWait = 100) {
    await new Promise((resolve) => setTimeout(resolve, maxWait));
  }

  async syncGifts() {
    this.lastSyncTime = Date.now();

    if (this.isSyncing) {
      while (this.isSyncing) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      return;
    }

    if (this.isGiftSyncPaused) return;

    this.isSyncing = true;

    try {
      const cart = await CartAPI.getCart();
      if (!this.hasAnyGiftConfigurations()) return;

      const currentGifts = this.getCurrentGiftsInCart(cart);
      const requiredGifts = this.calculateRequiredGifts(cart);

      if (this.giftsNeedUpdate(currentGifts, requiredGifts)) {
        const changes = this.calculateGiftChanges(currentGifts, requiredGifts);
        await this.executeGiftChanges(changes, cart);
        this.refreshCartUI();
      }
    } catch (error) {
      if (error.message?.includes("no valid id or line parameter")) {
        this.cleanupStaleConfigurations();
      }
    } finally {
      this.isSyncing = false;
    }
  }

  forceInitAndSync() {
    if (!this.isInitialized) this.init();
    setTimeout(() => this.syncGifts(), 100);
  }

  giftsNeedUpdate(current, required) {
    const currentIds = Object.keys(current).map(Number).sort();
    const requiredIds = Object.keys(required).map(Number).sort();

    return (
      JSON.stringify(currentIds) !== JSON.stringify(requiredIds) ||
      !Object.keys(required).every(
        (giftId) => current[giftId] === required[giftId]
      )
    );
  }

  getGiftVariantIds() {
    const configurations = this.getConfigurations();
    const giftVariantIds = new Set();

    Object.values(configurations).forEach((config) => {
      config.gifts?.forEach((gift) => {
        giftVariantIds.add(parseInt(gift.variant_id));
      });
    });

    return giftVariantIds;
  }

  getCurrentGiftsInCart(cart) {
    const giftVariantIds = this.getGiftVariantIds();
    const giftsInCart = {};

    cart.items.forEach((item) => {
      if (giftVariantIds.has(item.variant_id)) {
        giftsInCart[item.variant_id] = item.quantity;
      }
    });

    return giftsInCart;
  }

  calculateRequiredGifts(cart) {
    const configurations = this.getConfigurations();
    const requiredGifts = {};
    const productQuantities = this.getProductQuantitiesFromCart(cart);

    Object.entries(productQuantities).forEach(([productId, totalQuantity]) => {
      const config = configurations[productId];
      if (!config?.gifts) return;

      config.gifts
        .filter((gift) => gift.threshold <= totalQuantity)
        .forEach((gift) => {
          const giftVariantId = parseInt(gift.variant_id);
          requiredGifts[giftVariantId] =
            (requiredGifts[giftVariantId] || 0) + 1;
        });
    });

    return requiredGifts;
  }

  getProductQuantitiesFromCart(cart) {
    return cart.items.reduce((quantities, item) => {
      if (item.product_id) {
        quantities[item.product_id] =
          (quantities[item.product_id] || 0) + item.quantity;
      }
      return quantities;
    }, {});
  }

  calculateGiftChanges(current, required) {
    const toAdd = {};
    const toRemove = {};

    Object.entries(required).forEach(([giftId, requiredQty]) => {
      const currentQty = current[giftId] || 0;
      if (requiredQty > currentQty) {
        toAdd[giftId] = requiredQty - currentQty;
      }
    });

    Object.entries(current).forEach(([giftId, currentQty]) => {
      const requiredQty = required[giftId] || 0;
      if (currentQty > requiredQty) {
        toRemove[giftId] = currentQty - requiredQty;
      }
    });

    return { toAdd, toRemove };
  }

  async executeGiftChanges(changes, cart = null) {
    for (const [giftId, quantity] of Object.entries(changes.toAdd)) {
      await this.retry(() => CartAPI.addItem(parseInt(giftId), quantity));
    }

    if (Object.keys(changes.toRemove).length > 0) {
      const currentCart = cart || (await CartAPI.getCart());

      for (const [giftId, removeQuantity] of Object.entries(changes.toRemove)) {
        const giftVariantId = parseInt(giftId);
        const currentItem = currentCart.items.find(
          (item) => item.variant_id === giftVariantId
        );

        if (!currentItem) continue;

        const newQuantity = Math.max(0, currentItem.quantity - removeQuantity);

        try {
          await this.retry(() =>
            CartAPI.updateItem(currentItem.key, newQuantity)
          );
        } catch (error) {
          console.error(`Failed to remove gift ${giftVariantId}:`, error);
        }
      }
    }
  }

  setConfiguration(productId, gifts) {
    const configurations = this.getConfigurations();
    const hadConfigurations = Object.keys(configurations).length > 0;

    if (gifts?.length > 0) {
      const newConfig = {
        gifts: gifts.map((gift) => ({
          variant_id: gift.variant_id,
          title: gift.title,
          threshold: gift.threshold,
          image_url: gift.image_url || "",
          price: gift.price || "0.00",
        })),
        lastUpdated: Date.now(),
      };

      const existingConfig = configurations[productId];
      if (
        !existingConfig ||
        JSON.stringify(existingConfig.gifts) !== JSON.stringify(newConfig.gifts)
      ) {
        configurations[productId] = newConfig;
        this.saveConfigurations(configurations);
      }
    }

    this.handleConfigurationChange(
      hadConfigurations,
      Object.keys(configurations).length > 0
    );
  }

  handleConfigurationChange(hadConfigurations, hasConfigurations) {
    if (!hadConfigurations && hasConfigurations && !this.isInitialized) {
      this.init();
    }

    if (hasConfigurations && this.isInitialized) {
      setTimeout(() => this.syncGifts(), 100);
    }
  }

  getConfiguration(productId) {
    return this.getConfigurations()[productId] || null;
  }

  hasGiftConfiguration(productId) {
    return this.getConfiguration(productId) !== null;
  }

  removeConfiguration(productId) {
    const configurations = this.getConfigurations();
    if (configurations[productId]) {
      delete configurations[productId];
      this.saveConfigurations(configurations);
    }
  }

  getConfigurations() {
    try {
      const data = localStorage.getItem(this.storageKey);
      const configurations = data ? JSON.parse(data) : {};
      return this.validateConfigurations(configurations);
    } catch (error) {
      localStorage.removeItem(this.storageKey);
      return {};
    }
  }

  validateConfigurations(configurations) {
    const validConfigurations = {};

    Object.entries(configurations).forEach(([productId, config]) => {
      if (!config?.gifts?.length) return;

      const validGifts = config.gifts.filter(
        (gift) =>
          gift.variant_id &&
          typeof gift.threshold === "number" &&
          gift.threshold > 0
      );

      if (validGifts.length > 0) {
        validConfigurations[productId] = { ...config, gifts: validGifts };
      }
    });

    if (
      JSON.stringify(configurations) !== JSON.stringify(validConfigurations)
    ) {
      this.saveConfigurations(validConfigurations);
    }

    return validConfigurations;
  }

  saveConfigurations(configurations) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(configurations));
    } catch (error) {
      console.error("Error saving configurations:", error);
    }
  }

  cleanupOrphanedData() {
    const oldKeys = [
      "giftData",
      "allGiftData",
      "giftCount",
      "giftConfigurations",
    ];

    const allLocalStorageKeys = Object.keys(localStorage);
    const giftConfigKeysToRemove = allLocalStorageKeys.filter(
      (key) =>
        key.startsWith("giftConfigurations_") &&
        !key.startsWith("productGiftConfigurations_")
    );

    [...oldKeys, ...giftConfigKeysToRemove].forEach((key) => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
      }
    });

    this.cleanupDuplicateConfigurations();
    this.cleanupStaleConfigurations();
  }

  cleanupDuplicateConfigurations() {
    try {
      const configurations = this.getConfigurations();
      const cleanedConfigurations = {};
      let hasChanges = false;

      Object.entries(configurations).forEach(([productId, config]) => {
        if (this.isValidConfiguration(productId, config)) {
          cleanedConfigurations[productId] = config;
        } else {
          hasChanges = true;
        }
      });

      if (hasChanges) {
        this.saveConfigurations(cleanedConfigurations);
      }
    } catch (error) {
      console.error("Error cleaning up configurations:", error);
    }
  }

  isValidConfiguration(productId, config) {
    if (!productId || productId === "null" || productId === "undefined") {
      return false;
    }

    if (!config || typeof config !== "object") {
      return false;
    }

    if (
      !config.gifts ||
      !Array.isArray(config.gifts) ||
      config.gifts.length === 0
    ) {
      return false;
    }

    return config.gifts.every(
      (gift) =>
        gift.variant_id &&
        typeof gift.threshold === "number" &&
        gift.threshold > 0 &&
        gift.title
    );
  }

  cleanupStaleConfigurations() {
    try {
      const configurations = this.getConfigurations();
      const now = Date.now();
      const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
      let hasChanges = false;

      Object.entries(configurations).forEach(([productId, config]) => {
        if (!config.lastUpdated) {
          config.lastUpdated = now;
          hasChanges = true;
        } else if (config.lastUpdated < sevenDaysAgo) {
          delete configurations[productId];
          hasChanges = true;
        }
      });

      if (hasChanges) {
        this.saveConfigurations(configurations);
      }
    } catch (error) {
      console.error("Error cleaning up stale configurations:", error);
    }
  }

  showLoading() {
    const loader = document.querySelector(".loader");
    const cartDrawer = document.querySelector("#CartDrawer");

    if (loader) loader.style.display = "block";
    if (cartDrawer) cartDrawer.classList.add("loader-active");
  }

  hideLoading() {
    const loader = document.querySelector(".loader");
    const cartDrawer = document.querySelector("#CartDrawer");

    if (loader) loader.style.display = "none";
    if (cartDrawer) cartDrawer.classList.remove("loader-active");
  }

  showError(message) {
    console.error("Gift Manager Error:", message);
  }

  refreshCartUI() {
    if (window.Shopify?.theme?.cart) {

      window.Shopify.theme.cart.refresh();
      return;
    }

    document.dispatchEvent(new CustomEvent("cart:updated"));

    if (this.isCartPage()) {
      this.refreshCartPage();
    }

    const timeSinceLastSync = Date.now() - this.lastSyncTime;
    if (timeSinceLastSync > 200) {
      CartAPI.getCart()
        .then((cart) => this.updateCartElements(cart))
        .catch((error) => {
          console.error("Error updating cart count:", error);
          this.refreshCartDrawerManually();
        });
    } else {

    }

  }

  isCartPage() {
    return (
      window.location.pathname === "/cart" ||
      window.location.pathname.startsWith("/cart/") ||
      document.body.classList.contains("template-cart")
    );
  }

  refreshCartPage() {
    document.dispatchEvent(new CustomEvent("cart:refresh"));
    document.dispatchEvent(new CustomEvent("cart:updated"));

    setTimeout(() => {
      if (this.isCartPage()) {
        window.location.reload();
      }
    }, 50);
  }

  updateCartElements(cart) {
    const countElement = document.querySelector(".cart-count-bubble");
    const countTextElement = document.querySelector(
      ".cart-count-bubble > span"
    );

    if (countElement) {
      countTextElement.textContent = cart.item_count;
      if (cart.item_count <= 0) countElement.style.display = "none";
    }

    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      if (window.$?.fn.load) {
        window.$("cart-drawer").load(`${window.location.href} #CartDrawer`);
      } else {
        this.refreshCartDrawerManually();
      }
    }
  }

  async refreshCartDrawerManually() {
    try {
      const response = await fetch(window.location.href);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const newDrawer = doc.querySelector("#CartDrawer");
      const currentDrawer = document.querySelector("#CartDrawer");

      if (newDrawer && currentDrawer) {
        currentDrawer.innerHTML = newDrawer.innerHTML;
      }
    } catch (error) {
      console.error("Error in manual cart refresh:", error);
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  async retry(func, attempts = this.retryAttempts) {
    for (let i = 0; i < attempts; i++) {
      try {
        const result = await func();
        return result;
      } catch (error) {
        if (
          error.message &&
          error.message.includes("no valid id or line parameter")
        ) {
          return { items: [] };
        }

        if (i === attempts - 1) throw error;

        const delay = Math.pow(2, i) * 1000;
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }
}

function initializeGiftManager() {
  if (!window.giftManager) {
    window.giftManager = new GiftManager();
  }
  window.giftManager.init();
}

Object.assign(window, { GiftManager, CartAPI });

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeGiftManager);
} else {
  initializeGiftManager();
}

document.addEventListener("shopify:section:load", initializeGiftManager);
document.addEventListener("shopify:section:reorder", initializeGiftManager);

let currentUrl = window.location.href;
const observer = new MutationObserver(() => {
  if (window.location.href !== currentUrl) {
    currentUrl = window.location.href;
    setTimeout(initializeGiftManager, 100);
  }
});

observer.observe(document.body, {
  childList: true,
  subtree: true,
});

window.initializeGiftManager = initializeGiftManager;

